import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";



function Signup({ goToLogin }) {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    password: "",
  });
  const [focusedInput, setFocusedInput] = useState(null);
  const [animate, setAnimate] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setAnimate(true);
  }, []);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleChange = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phone);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    const { username, password, email, phone } = formData;

    if (!username || !password || !email || !phone) {
      alert("All fields are required.");
      return;
    }

    if (!validateEmail(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    if (!validatePhone(phone)) {
      alert("Please enter a valid 10-digit phone number.");
      return;
    }

    setIsProcessing(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/signup`, formData);
      
      // Store userId for payment
      const newUserId = res.data.user.id;
      setUserId(newUserId);
      
      // Show payment modal
      setShowPayment(true);
      setIsProcessing(false);
      
      // Initiate payment immediately
      handlePayment(newUserId);
    } catch (err) {
      setIsProcessing(false);
      const msg = err.response?.data?.message || "Signup failed. Try again.";
      alert(msg);
    }
  };

  const handlePayment = async (userIdToUse) => {
    try {
      setIsProcessing(true);
      
      // Create Razorpay order
      const orderRes = await axios.post(`${API_BASE_URL}/create-order`, {
        userId: userIdToUse,
      });

      const { orderId, amount, currency, keyId } = orderRes.data;

      // Razorpay options
      const options = {
        key: keyId,
        amount: amount,
        currency: currency,
        name: "File Transfer Service",
        description: "Account Activation Fee",
        order_id: orderId,
        handler: async function (response) {
          // Payment successful - verify it
          try {
            await axios.post(`${API_BASE_URL}/verify-payment`, {
              userId: userIdToUse,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            alert("Payment successful! Your account is now active. Please login.");
            
            // Clear form and go to login
            setFormData({
              username: "",
              password: "",
              email: "",
              phone: "",
            });
            setShowPayment(false);
            setIsProcessing(false);
            goToLogin();
          } catch (err) {
            setIsProcessing(false);
            alert("Payment verification failed. Please contact support.");
          }
        },
        prefill: {
          name: formData.username,
          email: formData.email,
          contact: formData.phone,
        },
        theme: {
          color: "#1f2937",
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
            alert("Payment cancelled. Your account is created but inactive. Please complete payment to login.");
            goToLogin();
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setIsProcessing(false);
      console.error("Payment error:", err);
      alert("Failed to initiate payment. Please try again.");
    }
  };

  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      backgroundColor: "#fafafa",
      padding: "20px",
    },
    form: {
      width: "100%",
      maxWidth: "340px",
      backgroundColor: "#fff",
      padding: "clamp(24px, 5vw, 36px)",
      borderRadius: "16px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
      opacity: animate ? 1 : 0,
      transform: animate ? "scale(1)" : "scale(0.95)",
      transition: "all 0.5s ease",
    },
    iconContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "clamp(16px, 4vw, 24px)",
      animation: animate ? "rotate 1s ease-out" : "none",
    },
    iconOuter: {
      width: "clamp(70px, 15vw, 90px)",
      height: "clamp(70px, 15vw, 90px)",
      borderRadius: "50%",
      backgroundColor: "#f3f4f6",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    iconInner: {
      width: "clamp(55px, 12vw, 70px)",
      height: "clamp(55px, 12vw, 70px)",
      borderRadius: "50%",
      backgroundColor: "#1f2937",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
     iconImage: {
      width: '44px',
      height: '24px',
      objectFit: 'contain'
    },
    iconText: {
      fontSize: "clamp(28px, 6vw, 36px)",
    },
    heading: {
      fontSize: "clamp(24px, 5vw, 28px)",
      fontWeight: "700",
      color: "#1f2937",
      marginBottom: "6px",
      textAlign: "center",
      letterSpacing: "-0.5px",
    },
    subheading: {
      fontSize: "clamp(14px, 3vw, 15px)",
      color: "#6b7280",
      textAlign: "center",
      marginBottom: "clamp(20px, 4vw, 28px)",
    },
    inputWrapper: {
      marginBottom: "clamp(16px, 3vw, 20px)",
    },
    label: {
      fontSize: "clamp(13px, 2.5vw, 14px)",
      fontWeight: "600",
      color: "#374151",
      marginBottom: "8px",
      display: "block",
    },
    input: {
      width: "100%",
      height: "clamp(44px, 8vw, 48px)",
      padding: "0 14px",
      borderWidth: "1.5px",
      borderStyle: "solid",
      borderColor: "#e5e7eb",
      borderRadius: "10px",
      fontSize: "clamp(14px, 3vw, 15px)",
      color: "#1f2937",
      backgroundColor: "#fafafa",
      outline: "none",
      boxSizing: "border-box",
      transition: "all 0.3s",
    },
    inputFocused: {
      borderColor: "#1f2937",
      backgroundColor: "#fff",
    },
    button: {
      width: "100%",
      height: "clamp(44px, 8vw, 48px)",
      backgroundColor: "#1f2937",
      borderRadius: "10px",
      color: "#fff",
      fontSize: "clamp(15px, 3vw, 16px)",
      fontWeight: "600",
      letterSpacing: "0.3px",
      border: "none",
      cursor: "pointer",
      marginTop: "8px",
      marginBottom: "clamp(16px, 3vw, 20px)",
      transition: "background-color 0.3s",
      opacity: isProcessing ? 0.6 : 1,
    },
    buttonHover: {
      backgroundColor: "#374151",
    },
    loginContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "4px",
    },
    loginText: {
      fontSize: "clamp(13px, 2.5vw, 14px)",
      color: "#6b7280",
    },
    loginLink: {
      fontSize: "clamp(13px, 2.5vw, 14px)",
      color: "#1f2937",
      fontWeight: "600",
      cursor: "pointer",
      textDecoration: "none",
    },
    paymentInfo: {
      backgroundColor: "#f0fdf4",
      border: "1px solid #86efac",
      borderRadius: "8px",
      padding: "clamp(10px, 2vw, 12px)",
      marginBottom: "clamp(16px, 3vw, 20px)",
      fontSize: "clamp(12px, 2.5vw, 13px)",
      color: "#166534",
      textAlign: "center",
    },
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @media (max-width: 480px) {
            body {
              font-size: 14px;
            }
          }
        `}
      </style>
      <form onSubmit={handleSignup} style={styles.form}>
        <div style={styles.iconContainer}>
          <div style={styles.iconOuter}>
            <div style={styles.iconInner}>
              {/* <span style={styles.iconText}>✨</span> */}
               <img 
                src="/images/logo.jpeg" 
                alt="Logo" 
                style={styles.iconImage}
                className="icon-image"
              />
            </div>
          </div>
        </div>

        <h2 style={styles.heading}>Create Account</h2>
        <p style={styles.subheading}>Sign up to get started</p>

        {/* <div style={styles.paymentInfo}>
          💳 One-time activation fee: ₹500
        </div> */}

        <div style={styles.inputWrapper}>
          <label style={styles.label}>Username</label>
          <input
            style={{
              ...styles.input,
              ...(focusedInput === "username" ? styles.inputFocused : {}),
            }}
            placeholder="Enter username"
            value={formData.username}
            onChange={(e) => handleChange("username", e.target.value)}
            onFocus={() => setFocusedInput("username")}
            onBlur={() => setFocusedInput(null)}
            autoComplete="off"
            disabled={isProcessing}
          />
        </div>

        <div style={styles.inputWrapper}>
          <label style={styles.label}>Email</label>
          <input
            style={{
              ...styles.input,
              ...(focusedInput === "email" ? styles.inputFocused : {}),
            }}
            placeholder="Enter email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            onFocus={() => setFocusedInput("email")}
            onBlur={() => setFocusedInput(null)}
            autoComplete="off"
            disabled={isProcessing}
          />
        </div>

        <div style={styles.inputWrapper}>
          <label style={styles.label}>Phone</label>
          <input
            style={{
              ...styles.input,
              ...(focusedInput === "phone" ? styles.inputFocused : {}),
            }}
            placeholder="Enter phone number"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            onFocus={() => setFocusedInput("phone")}
            onBlur={() => setFocusedInput(null)}
            autoComplete="off"
            disabled={isProcessing}
          />
        </div>

        <div style={styles.inputWrapper}>
          <label style={styles.label}>Password</label>
          <input
            style={{
              ...styles.input,
              ...(focusedInput === "password" ? styles.inputFocused : {}),
            }}
            placeholder="Enter password"
            type="password"
            value={formData.password}
            onChange={(e) => handleChange("password", e.target.value)}
            onFocus={() => setFocusedInput("password")}
            onBlur={() => setFocusedInput(null)}
            autoComplete="off"
            disabled={isProcessing}
          />
        </div>

        <button
          style={styles.button}
          type="submit"
          disabled={isProcessing}
          onMouseOver={(e) => !isProcessing && (e.target.style.backgroundColor = styles.buttonHover.backgroundColor)}
          onMouseOut={(e) => (e.target.style.backgroundColor = styles.button.backgroundColor)}
        >
          {isProcessing ? "Processing..." : "Continue to Payment"}
        </button>

        <div style={styles.loginContainer}>
          <span style={styles.loginText}>Already have an account?</span>
          <span style={styles.loginLink} onClick={goToLogin}>
            Log In
          </span>
        </div>
      </form>
    </div>
  );
}

export default Signup;
